import {
  fetchPrivateMoneyAccountHook,
  updatePrivateMoneyAccountHook,
} from 'admin/actions/private-money-account-hooks';
import Headings from 'admin/components/atoms/Headings';
import Label from 'admin/components/atoms/Label';
import Labeled from 'admin/components/atoms/Labeled';
import BreadCrumb, { Crumb } from 'admin/components/molecules/BreadCrumb';
import { ensureInteger, toQueryParams } from 'admin/utils';
import moment from 'moment';
import React, { ComponentProps, useEffect, useState } from 'react';
import styled from 'styled-components';
import Loader from '../../atoms/Loader';
import ApiResults from '../../molecules/ApiResults';
import MoneyAccountHookModal from '../../pages/MoneyAccountHookModal';
import EditMoneyAccountHook, { MoneyAccountHookForm } from './EditMoneyAccountHook';

type MoneyAccountHookDetails = ComponentProps<typeof EditMoneyAccountHook>['moneyAccountHookForm'];

const FormWrapper = styled.div``;

const ModalWrapper = styled.div`
  ${Labeled} {
    display: grid;
    grid-template-columns: 1fr 4fr;
    grid-template-rows: auto;
    justify-items: left;
    margin-top: 10px;
  }
  ${Labeled} > ${Label} {
    grid-column: 1;
  }
  max-height: calc(100vh - 210px);
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: #fff;
    border-left: solid 1px #ececec;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
    box-shadow: inset 0 0 0 2px #fff;
  }
`;

const ModalButtonsWrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
`;

const MoneyAccountHookDetail = ({ match }) => {
  const moneyId = match.url.split('/')[3];
  const moneyAccountHookId = match.params.id;
  const crumb = [
    new Crumb('ホーム', '/', false),
    new Crumb('販促管理', '', true),
    new Crumb('キャンペーン管理', '/promotion/campaigns', false),
    new Crumb('ウォレット追加時キャンペーン詳細', '', true),
  ];
  const datetimeFormat = 'YYYY/MM/DD HH:mm:ss';

  const [pmaHook, setPmaHook] = useState<MoneyAccountHookDetails | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiMessages, setApiMessages] = useState<{ text: string; isError?: boolean }[]>([]);
  const [requestParam, setRequestParam] = useState<MoneyAccountHookForm | null>(null);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onUpdatePmaHook = (data: MoneyAccountHookForm) => {
    setRequestParam(data);
    openModal();
  };

  const onConfirm = () => {
    if (!requestParam) return;
    const r = requestParam;
    const updated = {
      private_money_id: moneyId,
      name: r.name,
      description: r.description,
      starts_at: r.dateRange?.from,
      ends_at: r.dateRange?.to,
      usage_limit: ensureInteger(r.usageLimit),
      point_amount: ensureInteger(r.pointAmount),
      point_expires_at: r.pointExpiresAt,
      point_expires_in_days: r.pointExpiresInDays // 本来足し引きしないで受け渡しサーバ内部計算で対処すべき
        ? (ensureInteger(r.pointExpiresInDays) ?? 0) + 1
        : null,
    };

    updatePrivateMoneyAccountHook(moneyAccountHookId, updated)
      .then(() => {
        setApiMessages([
          {
            text: 'Registered a campaign: キャンペーンを更新しました',
          },
        ]);
        setPmaHook({
          ...requestParam,
          id: pmaHook?.id,
          is_disabled: pmaHook?.is_disabled,
          bearPointAccount: pmaHook?.bearPointAccount,
          usage_count: pmaHook?.usage_count,
        });
      })
      .catch((err) => {
        err.then((er) =>
          setApiMessages([
            {
              text: `Could not registered a campaign: ${er.message}.`,
              isError: true,
            },
          ])
        );
      });

    closeModal();
  };

  useEffect(() => {
    const query = toQueryParams({ private_money_id: moneyId });
    fetchPrivateMoneyAccountHook(moneyAccountHookId, query).then((data) => {
      setPmaHook({
        id: data.id,
        name: data.name,
        description: data.description ?? '',
        dateRange: {
          from: data.starts_at ? moment(data.starts_at) : null,
          to: data.ends_at ? moment(data.ends_at) : null,
        },
        usageLimit: data.usage_limit ?? undefined,
        pointAmount: data.point_amount ?? null,
        pointExpiresAt: data.point_expires_at ? moment(data.point_expires_at) : undefined,
        pointExpiresInDays: data.point_expires_in_days ? data.point_expires_in_days - 1 : undefined,
        senderAccount: data.sender_account,
        senderShop: data.sender,
        is_disabled: data.is_disabled,
        bearPointAccount: data.bear_point_account,
        usage_count: data.usage_count,
      });
    });
  }, []);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={crumb} />
      <Headings.h5>キャンペーン情報</Headings.h5>
      {pmaHook ? (
        <EditMoneyAccountHook
          moneyAccountHookForm={pmaHook}
          onSubmit={onUpdatePmaHook}
          mode="edit"
        />
      ) : (
        <Loader type="Oval" width={50} height={50} />
      )}
      <ApiResults messages={apiMessages} />
      <MoneyAccountHookModal
        isOpen={isModalOpen}
        accountHook={requestParam}
        comparingAccountHook={pmaHook}
        onClose={() => setIsModalOpen(false)}
        onConfirm={onConfirm}
      />
    </FormWrapper>
  );
};

export default MoneyAccountHookDetail;
